import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiBatch, unwrapBatch } from '@/api/batch';
import type {
    LedgerListResponse,
    Collection,
    Tag,
    ProfileResponse,
    LedgerType,
} from '@/api/types';
import { resolveApiPath } from '@/contexts/GoApiContext';

interface UseBatchLedgersParams {
    type: LedgerType;
}

/**
 * Batch response data returned by the hook.
 */
export interface BatchLedgersData {
    ledgers: LedgerListResponse;
    collections: { items: Collection[] };
    tags: { items: Tag[] };
    profile: ProfileResponse;
}

/**
 * Hook to fetch ledgers, collections, tags, and profile in a single batch request.
 * This minimizes API calls and Lambda invocations.
 * Collections and tags are filtered by type to only show relevant options.
 *
 * Note: Always fetches all ledgers including archived for correct P&L calculation.
 * The UI filters archived ledgers for display, but P&L includes all.
 * Totals are calculated on the frontend from ledger items for performance.
 */
export function useBatchLedgers({ type }: UseBatchLedgersParams) {
    // Always include archived ledgers for correct P&L calculation
    const ledgersPath = `/api/ledgers?type=${type}&limit=0&includeArchived=true`;
    const collectionsPath = resolveApiPath('/api/collections') + `?type=${type}`;
    const tagsPath = resolveApiPath('/api/tags') + `?type=${type}`;

    return useQuery({
        queryKey: ['batch-ledgers', { type }],
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await apiBatch<{
                ledgers: LedgerListResponse;
                collections: { items: Collection[] };
                tags: { items: Tag[] };
                profile: ProfileResponse;
            }>([
                { id: 'ledgers', path: ledgersPath },
                { id: 'collections', path: collectionsPath },
                { id: 'tags', path: tagsPath },
                { id: 'profile', path: resolveApiPath('/api/profile/me') },
            ]);

            return {
                ledgers: unwrapBatch(result.responses.ledgers, 'ledgers'),
                collections: unwrapBatch(result.responses.collections, 'collections'),
                tags: unwrapBatch(result.responses.tags, 'tags'),
                profile: unwrapBatch(result.responses.profile, 'profile'),
            } as BatchLedgersData;
        },
    });
}
